import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { Button, buttonStyles } from '@psspl/ui';
import { cn } from '@psspl/utils';

import { CompanyLogo } from '../brand/company-logo';
import { ROUTES } from '../../lib/routes';

export interface RouteErrorPageProps {
  /**
   * `page` fills the viewport and is used above the app shell (sign-in, session
   * restore). `inline` renders inside the shell, so the header and navigation stay
   * usable when one screen fails.
   */
  readonly variant?: 'page' | 'inline';
}

/**
 * Route error boundary.
 *
 * Used as a React Router `errorElement`, which is the router's own error boundary:
 * it catches render errors, failed lazy-chunk loads and loader errors for its
 * subtree, so no class component is needed.
 *
 * `docs/security.md` forbids leaking internals, so the raw error is never shown.
 * The copy distinguishes only the one case a user can act on: a stale bundle after
 * a deployment, where reloading fetches the new chunks.
 */
export function RouteErrorPage({ variant = 'inline' }: RouteErrorPageProps) {
  const error = useRouteError();
  const isStaleBundle = isChunkLoadError(error);
  const isMissing = isRouteErrorResponse(error) && error.status === 404;

  const title = isStaleBundle
    ? 'A newer version of the portal is available'
    : isMissing
      ? 'That page does not exist'
      : 'This screen could not be loaded';

  const message = isStaleBundle
    ? 'Reload the page to continue with the latest version.'
    : 'The problem has been contained. Reload the page, or return to the project catalogue.';

  return (
    <div
      className={cn(
        'flex items-center justify-center px-4',
        variant === 'page' ? 'min-h-dvh bg-surface-base py-16' : 'min-h-[60dvh] py-8',
      )}
    >
      <div
        role="alert"
        className="flex w-full max-w-md flex-col items-center gap-5 rounded-lg border border-line-subtle bg-surface-raised px-6 py-8 text-center"
      >
        <CompanyLogo size="md" />

        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium uppercase tracking-widest text-accent-400">
            {isStaleBundle ? 'Update available' : 'Something went wrong'}
          </p>
          <h1 className="text-xl font-semibold tracking-tight text-ink-primary">{title}</h1>
          <p className="text-sm leading-relaxed text-ink-secondary">{message}</p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
          <Link to={ROUTES.projects} className={buttonStyles({ variant: 'secondary' })}>
            Go to projects
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Vite surfaces a missing lazy chunk as a failed dynamic import. */
function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    /dynamically imported module|Importing a module script failed/iu.test(error.message)
  );
}
